import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import Spinner from "./common/Spinner";
import { AppProvider, useAppContext } from "./context/AppContext";
import OktaSignInWidget from "./login/OktaSignInWidget";
import AuthLicense from "./generics/authorization/AuthLicense";
import AppHeader from "./generics/AppHeader";
import PageNotFound from "./common/PageNotFound";
import _ from "lodash";
import { useMount } from "./utils/genericCommon";
import {
  isDefaultHeader,
  isReport,
  removeLocalStorageFlag,
  setLocalStorageFlag,
  getLocalStorageFlag,
  getConvergedCookie,
} from "../src/utils/common";
import { removeRumUser } from "../src/monitoring";
import { getCookie, deleteCookies } from "./serviceWorker";
import { oktanonIdpConfig, oktaIdpConfig } from "./login/okta";
import modules from "./config/routeConfig";
import apiService from "./config/api-config";
import I18nProvider from "./i18n/I18nProvider";
import { Provider } from "react-redux";
import css from "./AppMagnetic.less";
import { BASE_URL } from "./apis/apiConstants";
import { routeURLs } from "./utils/enums";
import actionTypes from "./generics/AppHOC/actionTypes";
import store from "./store";
import {
  finalizeToggleRouteTracking,
  isApplicationMFERoute,
  prepareNavigationToggleView,
} from "./utils/toggleView";
import {
  SESSION_KEEP_ALIVE_ACTIVITY_EVENTS,
  SESSION_KEEP_ALIVE_INTERVAL_MS,
  shouldKeepSessionAlive,
} from "./utils/sessionKeepAlive";
import {
  ColorBlindFilterDefinitions,
  useConvergenceColorBlindMode,
} from "./common/Theme";
import { getVManageMonitorUrl } from "./utils/vManageNavigation";

const [HbrBanner, HbrIcon] = reactWrapper(["hbr-banner", "hbr-icon"]);

//Unify UI MFE imports
import { loadMFEConfig } from "./generics/mfe/UnifyMFEManifestService";
import RouterWrapperService from "./generics/mfe/UnifyMFEReactRouterWrapper";

const isReportGenerationUI =
  process.env.REACT_APP_SKIP_RBAC_DISABLE_RBAC === "FALSE" ? false : true;
const HomeSpinner = () => (
  <div className="home-spinner">
    <Spinner />
  </div>
);

//Inner component that has access to Router context
const AppContent = () => {
  const [token, setToken] = useState(null);
  const [loginState, setLoginState] = useState(null);
  const [promptLoginState, setPromptLoginState] = useState(null);
  const [sessionLogout, setSessionLogout] = useState(null);
  const [notification, setNotification] = useState([]);
  const [about, setAbout] = useState();
  const isUIconvergence = getCookie("cl-analytics");
  const mount = useMount();
  const { showAppSpinnerState } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const reloadTimeoutRef = useRef();
  const toggleRoutePathRef = useRef(location.pathname);
  const keepAliveIntervalRef = useRef(null);
  const isDocumentVisibleRef = useRef(true);
  const hadActivitySinceKeepAliveRef = useRef(false);

  useMemo(() => {
    RouterWrapperService.initialize(navigate);
  }, []);

  useConvergenceColorBlindMode(isUIconvergence);

  useEffect(() => {
    removeLocalStorageFlag("isTunnelTabVisible");
    // Adding window event listener for tracking active history entry changes while the user navigates the session history.
    window.addEventListener("popstate", historyChanage, false);

    return () => window.removeEventListener("popstate", historyChanage, false);
  }, []);

  useEffect(() => {
    if (!isUIconvergence) return undefined;

    const trackUserActivity = () => {
      hadActivitySinceKeepAliveRef.current = true;
    };

    const syncVisibilityState = () => {
      isDocumentVisibleRef.current = document.visibilityState !== "hidden";
    };

    const keepSessionAliveOnActivity = () => {
      if (
        shouldKeepSessionAlive({
          isDocumentVisible: isDocumentVisibleRef.current,
          hadActivitySinceInterval: hadActivitySinceKeepAliveRef.current,
        })
      ) {
        apiService.keepSessionAlive();
      }
      hadActivitySinceKeepAliveRef.current = false;
    };

    syncVisibilityState();

    SESSION_KEEP_ALIVE_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, trackUserActivity);
    });
    document.addEventListener("visibilitychange", syncVisibilityState);
    keepAliveIntervalRef.current = window.setInterval(
      keepSessionAliveOnActivity,
      SESSION_KEEP_ALIVE_INTERVAL_MS,
    );

    return () => {
      SESSION_KEEP_ALIVE_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, trackUserActivity);
      });
      document.removeEventListener("visibilitychange", syncVisibilityState);
      if (keepAliveIntervalRef.current) {
        window.clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
      hadActivitySinceKeepAliveRef.current = false;
    };
  }, [isUIconvergence]);

  useEffect(() => {
    if (isReportGenerationUI === false) {
      getState();
      if (!isUIconvergence) loadMFEConfig();
    }
  }, []);

  useEffect(() => {
    const previousPath = toggleRoutePathRef.current;
    const nextPath = location.pathname;

    RouterWrapperService.notifyRouteChange(nextPath);

    // Application MFE routes own toggle initialization and navigation.
    if (isApplicationMFERoute(nextPath)) {
      toggleRoutePathRef.current = nextPath;
      return;
    }

    const nextToggleView = prepareNavigationToggleView(previousPath, nextPath);
    const currentToggleView =
      store.getState().vanalytics?.app?.globalFilter?.toggleView;

    if (nextToggleView && currentToggleView !== nextToggleView) {
      store.dispatch({
        type: actionTypes.SET_TOGGLE,
        payload: nextToggleView,
      });
    }

    toggleRoutePathRef.current = nextPath;
  }, [location.pathname]);

  useEffect(() => {
    if (!isApplicationMFERoute(location.pathname)) {
      finalizeToggleRouteTracking(location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    const initializeConvergedUI = async () => {
      if (!window._convergence_version) {
        window._convergence_version = "NA";
      }
      if (isUIconvergence && mount.initMount) {
        mount.initMount = false;

        const xsrfToken = await apiService.getvManageToken();
        // vmanage xsrf token
        if (xsrfToken.errorObject instanceof Object === false)
          setLocalStorageFlag("xsrfToken", xsrfToken.data);

        const aboutInfoResponse = await apiService.getAboutInfo();
        // vmanage about
        if (aboutInfoResponse.errorObject instanceof Object === false) {
          setAbout(aboutInfoResponse?.data?.data);
          // For Qualtrics and MFE version check
          window._convergence_version =
            aboutInfoResponse?.data?.data?.version || "NA";
        }
        loadMFEConfig();

        const tenantId = getConvergedCookie("tenantId");
        if (!_.isEmpty(tenantId)) {
          const vsessionToken = await apiService.getvManageVSession(tenantId);
          // vmanage session
          if (vsessionToken.errorObject instanceof Object === false)
            setLocalStorageFlag("Vsessionid", vsessionToken.data.VSessionId);
        }
      }
    };

    initializeConvergedUI();
  }, [token]);

  useEffect(() => {
    if (sessionLogout !== null && loginState === false)
      window.location.href = sessionLogout;
  }, [sessionLogout, loginState]);

  const reloadCurrentView = () => {
    const handleClick = async () => {
      //cancel all pending API requests before reloading
      if (apiService?.cancelAllRequests instanceof Function) {
        try {
          await apiService.cancelAllRequests();
        } catch (error) {
          console.warn("Failed to cancel pending API requests:", error);
        }
      }
      window.location.reload();
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  };

  const setTimeouts = () => {
    clearTimeouts();
    reloadTimeoutRef.current = setTimeout(reloadCurrentView, 3600000); //1 hour
  };

  const clearTimeouts = () => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const events = [
      "load",
      "click",
      "scroll",
      "keypress",
      "wheel",
      "visibilitychange",
    ];

    const resetTimeout = () => {
      setTimeouts();
    };

    events.forEach((event) => {
      window.addEventListener(event, resetTimeout);
    });

    setTimeouts();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimeout);
      });
      clearTimeouts();
    };
  }, []);

  // get csrf Token
  const getState = useCallback(async () => {
    const promptLogin = getCookie("prompt-login");
    const sessionLg = sessionStorage.getItem("cl-logout");
    const authState = getCookie("okta-oauth-state");
    removeLocalStorageFlag("Vsessionid");
    removeLocalStorageFlag("xsrfToken");

    const res = await apiService.getCsrfToken(authState);
    if (
      res.errorObject instanceof Object === false &&
      res.data instanceof Object
    ) {
      const data = res.data;
      oktanonIdpConfig.authParams.state = data["X-CSRFToken"];
      oktaIdpConfig.authParams.state = data["X-CSRFToken"];
      /* empty prompt-login cookie returns undefined in Mac OS and sometimes
       returns null or empty string in Windows OS.
       This condition checks if prompt-login cookie value is falsy.*/
      if (data.loginState === false && !promptLogin) {
        let redirectURL = "";
        //check if url is for converged ui and if so redirect to vManage because
        //you should not access vA url directly, but tru vManage
        if (isUIconvergence) redirectURL = getVManageMonitorUrl("/overview");
        else
          redirectURL =
            oktaIdpConfig.authParams.issuer +
            "/v1/authorize?client_id=" +
            oktaIdpConfig.clientId +
            "&response_type=code&scope=" +
            oktaIdpConfig.authParams.scopes.join("%20") +
            "&prompt=none&redirect_uri=" +
            oktaIdpConfig.redirectUri
              .replaceAll("/", "%2F")
              .replaceAll(":", "%3A") +
            "&state=" +
            data["X-CSRFToken"] +
            "&nonce=" +
            oktaIdpConfig.authParams.nonce;
        window.location.href = redirectURL; // redirect
      } else {
        setToken(data["X-CSRFToken"]);
        setLoginState(data.loginState);
        setPromptLoginState(promptLogin);
        setSessionLogout(sessionLg);
        if (data.loginState === false) {
          //get pre-login notification
          const res = await apiService.getPreLoginNotifications();
          if (
            res.errorObject instanceof Object === false &&
            res.data instanceof Object &&
            Array.isArray(res.data.data)
          )
            setNotification(res.data.data);
        }
      }
    }
  }, []);

  return (
    <>
      {isUIconvergence && <ColorBlindFilterDefinitions />}
      {/* App-level spinner overlay for SDWAN-Manager menu*/}
      {showAppSpinnerState && (
        <div className={css.appGlobalSpinner}>
          <Spinner />
        </div>
      )}
      {loginState === true || isReportGenerationUI === true ? (
        <div className={css.vanalyticsApp}>
          {isReportGenerationUI === false && <AuthLicense />}
          {!isDefaultHeader(location) && !isReport(location) ? (
            <div className="headerApp">
              <AppHeader urlPrefix={BASE_URL} token={token} about={about} />
            </div>
          ) : null}
          <div id="full-screen-content" className={css.vanalyticsPageContent}>
            <Routes>
              {modules.map((item) => {
                const Module = item.module;
                return (
                  <Route key={item.id} path={item.path} element={<Module />} />
                );
              })}
              {["/", "/analytics", `${BASE_URL}`].map((path) => (
                <Route
                  key={`spinner-overview-${path}`}
                  path={path}
                  element={
                    localStorage.getItem("currentOverlay") &&
                    !isUIconvergence ? (
                      <Navigate to={routeURLs.overview} replace />
                    ) : (
                      <HomeSpinner />
                    )
                  }
                />
              ))}
              <Route
                path="*"
                element={
                  getLocalStorageFlag("overlays") ? (
                    <PageNotFound />
                  ) : (
                    <HomeSpinner />
                  )
                }
              />
            </Routes>
          </div>
        </div>
      ) : promptLoginState === "true" && !sessionLogout ? (
        <Routes>
          <Route
            path={routeURLs.login}
            element={
              <div>
                {token && (
                  <>
                    {notification.length > 0 && (
                      <HbrBanner
                        open
                        product
                        variant="fill"
                        sentiment={
                          notification[0].notification_criticality === "info"
                            ? "info"
                            : "warning"
                        }
                      >
                        <HbrIcon
                          slot="icon"
                          name={
                            notification[0].notification_criticality === "info"
                              ? "alerts-info-large"
                              : "alerts-warning-large"
                          }
                        />
                        {notification[0].notification_msg}
                      </HbrBanner>
                    )}
                    <OktaSignInWidget
                      oktaIdpConfig={oktaIdpConfig}
                      oktanonIdpConfig={oktanonIdpConfig}
                      token={token}
                    />
                  </>
                )}
              </div>
            }
          />
          <Route path="*" element={<Navigate to={routeURLs.login} replace />} />
        </Routes>
      ) : (
        <HomeSpinner />
      )}
    </>
  );
};

// Main App component that wraps everything with Router
const App = () => {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
      </Router>
    </Provider>
  );
};

const historyChanage = async (event) => {
  event.preventDefault();
  const clVmanageURI = getCookie("cl-vManageURI");
  if (
    document.referrer !== "" &&
    clVmanageURI !== undefined &&
    !window.location.pathname.includes(`${BASE_URL}/`)
  ) {
    const isUIconvergence = getCookie("cl-analytics");
    if (isUIconvergence) {
      //below block is for UI convergence
      window.location.href = clVmanageURI;
    } else {
      //below block is for cross launch
      try {
        const res = await apiService.logout(); //logout from vAnalytics
        localStorage.removeItem("currentOverlay");
        localStorage.removeItem("currentOverlayName");
        localStorage.removeItem("okta-shared-transaction-storage");
        removeLocalStorageFlag("overlays");
        removeRumUser();
        deleteCookies();
        // After logout from vAnalytics redirect user to vManageURI
        if (res) window.location.href = clVmanageURI;
      } catch (err) {
        console.log(
          "Error while logging out and cleaning up localstorage and cookies",
          err,
        );
      }
    }
  }
};

const AppComponent = (props) => (
  <I18nProvider>
    <AppProvider>
      <App {...props} />
    </AppProvider>
  </I18nProvider>
);

export default AppComponent;
